import { Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import type { SiteSettings } from '@saas-bot/types';

import { Container, CtaButton, SmartLink } from './ui';

function BrandLogo({ settings }: { settings: SiteSettings }): JSX.Element {
  return (
    <NavLink to="/" className="flex items-center gap-2.5" aria-label={`${settings.brandName} home`}>
      {/* Light-stage variant of the mark — the stock icon is deep violet and
          disappears on the dark surface. See apps/website/public/icons/. */}
      <img
        src="/icons/vaanexa-icon-light.svg"
        alt=""
        width={40}
        height={32}
        className="h-8 w-auto object-contain"
      />
      <span className="text-lg font-bold tracking-[-0.02em] text-[var(--color-ink)]">
        {settings.brandName}
      </span>
    </NavLink>
  );
}

function Header({ settings }: { settings: SiteSettings }): JSX.Element {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  // Matches the marketing homepage bar: transparent over the hero, blurred dark
  // surface once the page moves.
  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const linkClass = ({ isActive }: { isActive: boolean }): string =>
    `text-sm font-medium transition-colors ${
      isActive ? 'accent-text' : 'text-[var(--color-ink-soft)] hover:text-[var(--color-ink)]'
    }`;

  return (
    <header
      className={`sticky top-0 z-40 transition-colors duration-300 ${
        scrolled || open
          ? 'border-b border-[var(--color-line)] bg-[rgba(5,6,13,0.72)] backdrop-blur-xl'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <Container className="flex h-16 items-center justify-between gap-4 sm:h-18">
        <BrandLogo settings={settings} />

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {settings.nav.map((item) =>
            item.href.startsWith('/') ? (
              <NavLink key={item.href} to={item.href} className={linkClass}>
                {item.label}
              </NavLink>
            ) : (
              <SmartLink
                key={item.href}
                href={item.href}
                className="text-sm font-medium text-[var(--color-ink-soft)] transition-colors hover:text-[var(--color-ink)]"
              >
                {item.label}
              </SmartLink>
            ),
          )}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          {settings.secondaryCta.label ? (
            <CtaButton link={settings.secondaryCta} variant="ghost" />
          ) : null}
          <CtaButton link={settings.primaryCta} variant="primary" />
        </div>

        <button
          type="button"
          className="grid h-10 w-10 place-items-center rounded-xl border border-[var(--color-line)] text-[var(--color-ink)] lg:hidden"
          onClick={() => setOpen((value) => !value)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
        </button>
      </Container>

      {open ? (
        <div className="border-t border-[var(--color-line)] bg-[rgba(5,6,13,0.97)] lg:hidden">
          <Container className="flex flex-col gap-1 py-4">
            {settings.nav.map((item) => (
              <SmartLink
                key={item.href}
                href={item.href}
                className={`rounded-xl px-3 py-3 text-base font-medium ${
                  location.pathname === item.href
                    ? 'accent-text bg-[var(--color-surface)]'
                    : 'text-[var(--color-ink-soft)]'
                }`}
              >
                {item.label}
              </SmartLink>
            ))}
            <div className="mt-3 flex flex-col gap-2">
              <CtaButton link={settings.primaryCta} variant="primary" />
              {settings.secondaryCta.label ? (
                <CtaButton link={settings.secondaryCta} variant="secondary" />
              ) : null}
            </div>
          </Container>
        </div>
      ) : null}
    </header>
  );
}

function Footer({ settings }: { settings: SiteSettings }): JSX.Element {
  return (
    <footer className="relative border-t border-[var(--color-line)]">
      <Container className="py-16">
        <div className="grid gap-10 md:grid-cols-[1.5fr_repeat(3,1fr)]">
          <div className="flex flex-col gap-4">
            <BrandLogo settings={settings} />
            <p className="max-w-xs text-sm leading-relaxed text-[var(--color-ink-soft)]">
              {settings.footer.tagline}
            </p>
            {settings.footer.socials.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-3">
                {settings.footer.socials.map((social) => (
                  <SmartLink
                    key={social.href}
                    href={social.href}
                    className="rounded-full border border-[var(--color-line)] px-4 py-1.5 text-xs font-semibold text-[var(--color-ink-soft)] transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]"
                  >
                    {social.label}
                  </SmartLink>
                ))}
              </div>
            ) : null}
          </div>

          {settings.footer.columns.map((column) => (
            <div key={column.heading}>
              <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-ink-muted)]">
                {column.heading}
              </h3>
              <ul className="mt-4 space-y-3">
                {column.links.map((link) => (
                  <li key={link.href}>
                    <SmartLink
                      href={link.href}
                      className="text-sm text-[var(--color-ink-soft)] transition-colors hover:text-[var(--color-ink)]"
                    >
                      {link.label}
                    </SmartLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 border-t border-[var(--color-line)] pt-6">
          <p className="text-sm text-[var(--color-ink-muted)]">{settings.footer.legalLine}</p>
        </div>
      </Container>
    </footer>
  );
}

export function Layout({
  settings,
  children,
}: {
  settings: SiteSettings;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="relative flex min-h-dvh flex-col">
      {/* Same ambient treatment as the marketing homepage: one masked grid, one
          soft light at the top of the page, one grain layer. */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute inset-0 mk-grid" />
        <div className="mk-glow-primary absolute -top-52 left-1/2 h-[560px] w-[900px] -translate-x-1/2" />
        <div className="absolute inset-0 mk-noise" />
      </div>
      <div className="relative flex min-h-dvh flex-col">
        <Header settings={settings} />
        <main className="flex-1">{children}</main>
        <Footer settings={settings} />
      </div>
    </div>
  );
}
